import { Box, SxProps, useTheme } from '@mui/material';
import { LayoutGroup } from 'framer-motion';
import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { Block } from '../../services/entities/impl/blfApi/schemas/block';
import { CropTypes } from '../../services/entities/impl/blfApi/schemas/variety';
import ChartAreaContent from '../charts/ChartAreaContent';
import ChartRanchView from '../charts/ChartRanchView';
import { flexCenter } from '../commonUI/cssClasses';
import RanchSummaryTableBlueberry from '../dataGridTables/ranchSummary/blueberry/RanchSummaryTableBlueberry';
import RanchSummaryTableGrape from '../dataGridTables/ranchSummary/grape/RanchSummaryTableGrape';
import { useFindNeighboringPoints } from '../imageViewer/hooks/useFindNeighboringPoints';
import ImageAreaContent from '../imageViewer/ImageAreaContent';
import Mapbox from '../map/Mapbox';
import OnboardingDialogs from '../onboarding/forms/blockBoundaryMapForm/BlockBoundaryOnboardDialog';
import SelectHeader from '../select/SelectHeader';
import {
  RanchViewFeaturesEnum,
  selectCropTypesOfFilteredBlocks,
  selectCurrBlockID,
  selectCurrCustomerID,
  selectCurrRanchID,
  selectCurrRanchViewFeature,
  setCurrCustomerByID,
  setCurrRanchByID,
} from '../select/selectSlice';
import RanchWidget from './components/RanchWidget/RanchWidget';
import useGetCropTypesOfFilteredBlocks from './hooks/useGetCropTypesOfFilteredBlocks';
import { useGetDropdownsData } from './hooks/useGetDropdownData';
import { useGetDropdownFilteredData } from './hooks/useGetDropdownFilteredData';
import useImagePointSelection from './hooks/useImagePointSelection';
import useRanchLayout from './hooks/useRanchLayout';
import { useRanchSummaryPageParams } from './hooks/useRanchSummaryPageParams';
import { useResetBlockDetail } from './hooks/useResetBlockDetail';
import { useResetPoints } from './hooks/useResetPoints';
import useSetCurrFeature from './hooks/useSetCurrFeature';
import {
  RanchAreaEnum,
  RanchPageViewEnum,
  selectRanchPageView,
  setRanchFocusArea,
  setRanchPageView,
} from './ranchesSlice';

const commonStyles: SxProps = {
  width: '100%',
  height: '100%',
  ...flexCenter,
};

const Ranches: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  // Local QueryParamsManager hook call
  useRanchSummaryPageParams();

  // Use Redux selectors directly
  const customerID = useAppSelector(selectCurrCustomerID);
  const ranchID = useAppSelector(selectCurrRanchID);
  const blockID = useAppSelector(selectCurrBlockID);
  const ranchPageView = useAppSelector(selectRanchPageView);

  // Still use Redux for these selectors that aren't URL parameters
  const currRanchViewFeature = useAppSelector(selectCurrRanchViewFeature);
  const cropTypesOfFilteredBlocks = useAppSelector(
    selectCropTypesOfFilteredBlocks
  );
  const pageSpacing = theme.spacing(2.5);

  const {
    customers,
    ranches,
    blocks,
    isCustomersLoading,
    isRanchesLoading,
    isBlocksLoading,
  } = useGetDropdownsData();

  const {
    customersFiltered,
    ranchesFiltered,
    blocksFiltered,
    blocksActive,
    scansFilteredByBlock,
    isLoadingScans,
  } = useGetDropdownFilteredData({
    customers,
    ranches,
    blocks,
  });

  useGetCropTypesOfFilteredBlocks(blocksFiltered, customerID);

  const showBlueberryTable = useMemo(() => {
    return cropTypesOfFilteredBlocks.includes(CropTypes.Blueberries);
  }, [cropTypesOfFilteredBlocks]);

  // Show grape table if the crop type is wine grapes, table grapes, juice grapes, vine nursery, or if no customer is selected since only the grape table has a no customer selected placeholder message so far until we rework the blueberry table to have a similar message.
  const showGrapeTable = useMemo(() => {
    return (
      cropTypesOfFilteredBlocks.includes(CropTypes.WineGrapes) ||
      cropTypesOfFilteredBlocks.includes(CropTypes.TableGrapes) ||
      cropTypesOfFilteredBlocks.includes(CropTypes.JuiceGrapes) ||
      cropTypesOfFilteredBlocks.includes(CropTypes.VineNursery) ||
      customerID === ''
    );
  }, [cropTypesOfFilteredBlocks, customerID]);

  // if there is only one customer, set it as the current customer
  useEffect(() => {
    if (customersFiltered?.length === 1) {
      dispatch(setCurrCustomerByID(customersFiltered[0].id));
    }
  }, [customersFiltered, dispatch]);

  // if there is only one ranch, set it as the current ranch
  useEffect(() => {
    if (ranchesFiltered?.length === 1) {
      dispatch(setCurrRanchByID(ranchesFiltered[0].id));
    }
  }, [ranchesFiltered, dispatch]);

  // The list of block buttons to show is slightly different than the list to show on the map.  We want to show all block buttons for a ranch instead of only showing the selected block so the user can easily switch between blocks in the ranch.
  const blocksFilteredButtons = useMemo(() => {
    // don't show any buttons if no ranch is selected
    let blocksFiltered: Block[] | undefined = undefined;
    if (ranchID !== '') {
      // If a ranch is selected, show all blocks in ranch
      blocksFiltered = blocksActive?.filter(
        (block) => block.ranchID === ranchID
      );
    } else if (customerID !== '' && ranchID === '' && blockID === '') {
      // If a customer is selected but no ranch or block, show all customer's blocks
      const ranchesFiltered = ranches?.filter(
        (ranch) => ranch.customerID === customerID
      );
      const ranchIDs = ranchesFiltered?.map((ranch) => ranch.id);
      blocksFiltered = blocksActive?.filter((block) =>
        ranchIDs?.includes(block.ranchID)
      );
    }

    return blocksFiltered;
  }, [ranchID, customerID, blockID, blocksActive, ranches]);

  // If customer or ranch is changed while in block view, set the ranch page view to ranch summary and reset the focused area
  useEffect(() => {
    if (ranchPageView === RanchPageViewEnum.scansPage) {
      dispatch(setRanchFocusArea(null));
    }
    if (ranchPageView === RanchPageViewEnum.blockDetail && blockID === '') {
      dispatch(setRanchFocusArea(null));
      dispatch(setRanchPageView(RanchPageViewEnum.ranchSummary));
    }
  }, [customerID, ranchID, blockID, ranchPageView, dispatch]);

  // Pick a point automatically if one is not selected when new data is loaded so that the image viewer is not blank.
  // Also set the current fields (rowNum, rowsideId, viewDirection, pointId) if they are not set.
  useImagePointSelection();
  // Find the neighboring points for the currently selected point
  useFindNeighboringPoints();
  // Reset the block detail view when in ranch summary view or when changing scans to avoid showing stale data
  useResetBlockDetail();
  // Reset the points when changing scans to avoid showing stale data
  useResetPoints();
  // Sets the current AI feature the the first available feature, else sets it to the default FeatureEnum.none
  useSetCurrFeature();
  // Adjusts grid area, template rows, and columns based on the selected ranch page view and focus area.
  const { gridArea, gridTemplateColumns, gridTemplateRows } = useRanchLayout();

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
        height: '100vh',
        maxHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'min-content 1fr',
        gridTemplateAreas: `
        'headerArea'
        'ranchContentArea'
        `,
      }}
    >
      <Box
        sx={{
          ...commonStyles,
          gridArea: 'headerArea',
        }}
      >
        <SelectHeader
          customers={customersFiltered}
          ranchesFiltered={ranchesFiltered}
          blocksFiltered={blocksFilteredButtons}
          scansFiltered={scansFilteredByBlock}
          isLoadingCustomers={isCustomersLoading}
          isLoadingRanches={isRanchesLoading}
          isLoadingBlocks={isBlocksLoading}
          isLoadingScans={isLoadingScans}
        />
      </Box>
      <Box
        sx={{
          gridArea: 'ranchContentArea',
          width: '100%',
          minWidth: '100%',
          maxWidth: '100%',
          height: '100%',
          minHeight: '100%',
          maxHeight: '100%',
          padding: `0 ${pageSpacing} ${pageSpacing}`,
          bgcolor: (theme) => theme.colors.canopy.background.default,
          display: 'grid',
          gridTemplateColumns,
          gridTemplateRows,
          gridGap: pageSpacing,
          gridTemplateAreas: gridArea,
        }}
      >
        <LayoutGroup>
          <RanchWidget gridArea={RanchAreaEnum.chartArea}>
            <ChartAreaContent />
          </RanchWidget>
          <RanchWidget
            gridArea={RanchAreaEnum.mapArea}
            displayHeader={false}
            overflowHidden
            displayFullscreenToggle={
              ranchPageView !== RanchPageViewEnum.ranchSummary &&
              currRanchViewFeature !== RanchViewFeaturesEnum.Tables
            }
            minHeight='250px'
          >
            {ranchPageView === RanchPageViewEnum.blockDetail && <Mapbox />}
            {ranchPageView !== RanchPageViewEnum.blockDetail && (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {currRanchViewFeature === RanchViewFeaturesEnum.RanchMap && (
                  <Mapbox />
                )}
                {currRanchViewFeature === RanchViewFeaturesEnum.Charts && (
                  <ChartRanchView />
                )}
                {currRanchViewFeature === RanchViewFeaturesEnum.Tables && (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      overflowX: 'auto',
                      overflowY: 'auto',
                      border: `1px solid ${theme.colors.canopy.divider}`,
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gridTemplateRows:
                        showBlueberryTable && showGrapeTable
                          ? 'repeat(2, minmax(50rem, 1fr))'
                          : '1fr',
                      gridGap: '1rem',
                    }}
                  >
                    {showBlueberryTable && (
                      <RanchSummaryTableBlueberry
                        blocksFiltered={blocksFiltered}
                      />
                    )}
                    {/* Show grape table as default for now if crop not supported or still loading */}
                    {(showGrapeTable ||
                      (!showBlueberryTable && !showGrapeTable)) && (
                      <RanchSummaryTableGrape blocksFiltered={blocksFiltered} />
                    )}
                  </Box>
                )}
              </Box>
            )}
          </RanchWidget>
          <RanchWidget
            gridArea={RanchAreaEnum.imageArea}
            displayHeader={false}
            overflowHidden
            displayFullscreenToggle
          >
            <ImageAreaContent />
          </RanchWidget>
        </LayoutGroup>
      </Box>
      <OnboardingDialogs />
    </Box>
  );
};

export default Ranches;
